import { useState, useEffect, useMemo } from "react";
import { B, HD, PD } from "../../theme";
import { Constellation } from "../../shared/Constellation";
import { saveModuleCompletion, saveTags } from "../../lib/db";
import { BackArrow } from "../../shared/BackArrow";
import { ENTRY_BY_SPOKE, SPOKE_ACCENTS, SPOKE_LABELS } from "./data";

/* Entry teaser — single question, themed by the spoke's accent color.
   Rendered as a first-tap gate on each spoke (replacing what used to be
   the spoke's first submodule). Saves one module_completions row
   (is_entry_teaser=true) plus its tag rows, then hands back to the caller
   so the home web can pulse the activation and reveal the next spoke. */

export function EntryTeaser({ spokeKey, user, onComplete, onBack, header, onSkip, skipLabel = "Skip this one" }) {
  const q = ENTRY_BY_SPOKE[spokeKey];
  const accent = SPOKE_ACCENTS[spokeKey] || PD.text;

  const [v, setV] = useState(false);
  const [sel, setSel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { const t = setTimeout(() => setV(true), 60); return () => clearTimeout(t); }, []);

  const seed = useMemo(() => {
    let h = 0;
    for (let i = 0; i < spokeKey.length; i++) h = (h * 31 + spokeKey.charCodeAt(i)) | 0;
    return Math.abs(h) || 5701;
  }, [spokeKey]);

  if (!q) return null;

  const pick = async (opt) => {
    if (saving) return;
    setSel(opt.id);
    setSaving(true);
    setError(null);

    try {
      if (user) {
        await saveModuleCompletion(
          user.id,
          q.spoke,
          q.key,
          { question: q.question, selected: opt.id, label: opt.label },
          { isEntryTeaser: true }
        );

        const tagRows = (opt.tags || []).map((t) => ({
          tag_key: t.key,
          tag_value: opt.id,
          tag_type: t.type,
          source_module: q.key,
        }));
        if (tagRows.length) await saveTags(user.id, tagRows);
      }

      setTimeout(() => {
        onComplete?.({ spoke: q.spoke, selected: opt.id, label: opt.label });
      }, 320);
    } catch (e) {
      console.error("EntryTeaser save failed:", e);
      setError("Couldn't save that. Try again.");
      setSel(null);
      setSaving(false);
    }
  };

  // Hover lives in CSS behind (hover: hover), so a tap on a phone never
  // leaves an option looking picked. Colors come in as custom properties.
  const css = `
.et-opt{background:var(--et-bg);border:1px solid var(--et-bd);color:var(--et-fg);-webkit-tap-highlight-color:transparent;transition:background .2s ease,border-color .2s ease,opacity .2s ease}
.et-opt:focus-visible{outline:2px solid var(--et-hbd);outline-offset:2px}
@media (hover:hover){.et-opt:not(:disabled):not([aria-pressed="true"]):hover{background:var(--et-hbg);border-color:var(--et-hbd)}}`;

  return (
    <div style={{
      minHeight: "100vh", display: "flex", flexDirection: "column",
      padding: "max(12px, env(safe-area-inset-top)) 24px max(32px, env(safe-area-inset-bottom))",
      background: PD.bg, position: "relative", overflow: "hidden",
    }}>
      <style>{css}</style>
      <Constellation seed={seed} threads={4} accent={accent} luminous={true} style={{ opacity: 0.28 }} />

      {onBack && (
        <div style={{ maxWidth: 520, margin: "0 auto", width: "100%", position: "relative", zIndex: 1 }}>
          <BackArrow onClick={onBack} color={accent} disabled={saving} />
        </div>
      )}

      <div style={{
        maxWidth: 520, margin: "auto", width: "100%", position: "relative", zIndex: 1,
        padding: "24px 0",
        opacity: v ? 1 : 0, transform: v ? "none" : "translateY(10px)",
        transition: "opacity 0.5s ease, transform 0.5s ease",
        textAlign: "center",
      }}>
        {/* The part's name in its accent, like the first screen of each quiz.
            A caller's header (the first questions' little web) replaces it. */}
        {header || (
          <p style={{
            ...B, fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase",
            color: accent, fontWeight: 600, margin: "0 0 14px",
          }}>
            {SPOKE_LABELS[spokeKey]}
          </p>
        )}

        <h2 style={{
          ...HD, fontSize: "clamp(23px, 6.2vw, 30px)", fontWeight: 500, fontStyle: "normal",
          color: PD.text, margin: "0 auto 28px", lineHeight: 1.28,
          maxWidth: 440, textWrap: "balance",
        }}>
          {q.question}
        </h2>

        <div style={{
          display: "flex", flexDirection: "column", gap: 10,
          maxWidth: 380, margin: "0 auto",
        }}>
          {q.options.map((o) => {
            const active = sel === o.id;
            return (
              <button
                key={o.id}
                type="button"
                className="et-opt"
                aria-pressed={active}
                onClick={() => pick(o)}
                disabled={saving}
                style={{
                  ...B, fontSize: 15,
                  "--et-fg": active ? PD.bg : PD.text,
                  "--et-bg": active ? accent : `${accent}14`,
                  "--et-bd": active ? accent : `${accent}55`,
                  "--et-hbg": `${accent}26`,
                  "--et-hbd": `${accent}AA`,
                  minHeight: 48, padding: "12px 18px",
                  cursor: saving ? "default" : "pointer",
                  textAlign: "center", lineHeight: 1.35, borderRadius: 14,
                  opacity: saving && !active ? 0.5 : 1,
                  fontWeight: active ? 600 : 400,
                }}
              >
                {o.label}
              </button>
            );
          })}
        </div>

        {error && (
          <p role="alert" style={{ ...B, fontSize: 13, color: "#E08AA0", marginTop: 14 }}>
            {error}
          </p>
        )}

        {onSkip && (
          <button
            type="button"
            onClick={onSkip}
            disabled={saving}
            style={{
              ...B, fontSize: 14, color: PD.sub, background: "none", border: "none",
              marginTop: 18, padding: "12px 16px", minHeight: 44,
              cursor: saving ? "default" : "pointer", letterSpacing: "0.02em",
            }}
          >
            {skipLabel}
          </button>
        )}
      </div>
    </div>
  );
}
